// ** react imports
import React, { useEffect } from 'react';

// ** react router imports
import { useNavigate, useParams } from 'react-router-dom';

// ** validation imports
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// ** custom hooks imports
import {
  useGetNotificationById,
  useUpdateNotification,
} from '../../../hooks/apis/useNotification';

// ** components imports
import LoadingButton from '../../../components/common/Buttons/LoadingButton';
import NormalButton from '../../../components/common/Buttons/NormalButton';
import NormalInput from '../../../components/common/Inputs/NormalInput';
import SelectInput from '../../../components/common/Inputs/SelectInput';
import Textarea from '../../../components/common/Inputs/Textarea';
import DynamicKeyValueInput from '../../../partials/DynamicKeyValueInput';
import Loading from '../../../components/common/Loading';

// ** enums imports
import { BUTTON_THEMES } from '../../../enums/common';

const RenderFormSection = props => {
  const { children } = props;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg mb-6 overflow-hidden">
      <div className="p-4">{children}</div>
    </div>
  );
};

const NotificationUpdate = () => {
  // hooks
  const navigate = useNavigate();
  const { notificationId } = useParams();

  // Get notification by ID
  const {
    data: notificationData,
    isGetNotificationByIdLoading,
    getNotificationById,
  } = useGetNotificationById();

  // Update notification
  const { isUpdateNotificationLoading, updateNotification } =
    useUpdateNotification();

  // validation schema
  const validationSchema = yup.object().shape({
    title: yup
      .string()
      .required('Title is required')
      .max(200, 'Title must be at most 200 characters')
      .trim(),
    content: yup.string().required('Content is required').trim(),
    category: yup
      .string()
      .nullable()
      .oneOf(
        [
          'info',
          'warning',
          'error',
          'success',
          'maintenance',
          'general',
          'alert',
          'update',
        ],
        'Category must be one of the allowed values'
      ),
    priority: yup
      .string()
      .nullable()
      .oneOf(
        ['low', 'medium', 'high', 'critical'],
        'Priority must be low, medium, high, or critical'
      ),
    type: yup
      .string()
      .nullable()
      .oneOf(
        ['system', 'user', 'broadcast', 'reminder', 'alert'],
        'Type must be one of the allowed values'
      ),
    userId: yup
      .string()
      .nullable()
      .max(100, 'User ID must be at most 100 characters'),
    isRead: yup.boolean().nullable(),
    isBroadcast: yup.boolean().nullable(),
    metadata: yup.object().nullable(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
    defaultValues: {
      title: '',
      content: '',
      category: null,
      priority: 'medium',
      type: 'system',
      userId: '',
      isRead: false,
      isBroadcast: false,
      metadata: {},
    },
  });

  // Fetch notification data when component mounts
  useEffect(() => {
    if (notificationId) {
      getNotificationById(notificationId);
    }
  }, [notificationId, getNotificationById]);

  // Populate form with notification data when it's loaded
  useEffect(() => {
    if (notificationData) {
      reset({
        title: notificationData.title || '',
        content: notificationData.content || '',
        category: notificationData.category || null,
        priority: notificationData.priority || 'medium',
        type: notificationData.type || 'system',
        userId: notificationData.userId || '',
        isRead: notificationData.isRead || false,
        isBroadcast: notificationData.isBroadcast || false,
        metadata: notificationData.metadata || {},
      });
    }
  }, [notificationData]);

  const onSubmit = async data => {
    try {
      const response = await updateNotification({ id: notificationId, data });

      if (response.data?.success) {
        reset();
        setTimeout(() => {
          navigate('/notifications/list');
        }, 150);
      } else if (response.error) {
        console.error('Error updating notification:', response.error);
      }
    } catch (error) {
      console.error('Error updating notification:', error);
    }
  };

  const handleCancel = () => {
    navigate('/notifications/list');
  };

  const renderErrorMessage = error => {
    if (!error) return null;
    return <p className="mt-2 text-xs text-red-500">{error}</p>;
  };

  // Watch values for conditional rendering
  const isBroadcast = watch('isBroadcast');

  return (
    <div className="flex">
      <main className="grow">
        <div className="px-4 sm:px-6 lg:px-8 py-6 w-full max-w-[96rem] mx-auto">
          {/* Page header */}
          <div className="sm:flex sm:justify-between sm:items-center mb-6">
            {/* Left: Title */}
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                Update Notification
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Update the notification details
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white dark:bg-gray-800 shadow-xs rounded-xl relative overflow-y-auto">
            {isGetNotificationByIdLoading && (
              <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm z-40 flex items-center justify-center rounded-xl">
                <div className="flex items-center gap-3 text-gray-60 dark:text-gray-300">
                  <Loading />
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="p-6 space-y-6">
                <RenderFormSection>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <NormalInput
                        label="Title"
                        type="text"
                        name="title"
                        register={register}
                        errors={errors.title?.message}
                        required
                        maxLength={200}
                        placeholder="Enter notification title"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Textarea
                        label="Content"
                        name="content"
                        register={register}
                        errors={errors.content?.message}
                        required
                        placeholder="Enter notification content"
                        rows={4}
                      />
                    </div>

                    <div>
                      <SelectInput
                        label="Priority"
                        name="priority"
                        options={[
                          { label: 'Low', value: 'low' },
                          { label: 'Medium', value: 'medium' },
                          { label: 'High', value: 'high' },
                          { label: 'Critical', value: 'critical' },
                        ]}
                        register={register}
                        errors={errors.priority?.message}
                      />
                    </div>

                    <div>
                      <SelectInput
                        label="Type"
                        name="type"
                        options={[
                          { label: 'System', value: 'system' },
                          { label: 'User', value: 'user' },
                          { label: 'Broadcast', value: 'broadcast' },
                          { label: 'Reminder', value: 'reminder' },
                          { label: 'Alert', value: 'alert' },
                        ]}
                        register={register}
                        errors={errors.type?.message}
                      />
                    </div>
                  </div>
                </RenderFormSection>

                {/* Advanced Settings Section */}
                <RenderFormSection>
                  <div className="space-y-1">
                    <label
                      htmlFor="metadata"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Metadata
                    </label>
                    <Controller
                      name="metadata"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <DynamicKeyValueInput
                          value={value ? value : {}}
                          onChange={onChange}
                          placeholder="Enter metadata as key-value pairs"
                          error={errors.metadata?.message}
                        />
                      )}
                    />
                  </div>
                </RenderFormSection>

                <footer>
                  <div className="flex flex-col px-6 py-5 border-t border-gray-200 dark:border-gray-700/60">
                    <div className="flex self-end gap-3">
                      <NormalButton
                        theme={BUTTON_THEMES.DANGER}
                        onClick={() => handleCancel()}
                      >
                        Cancel
                      </NormalButton>
                      <LoadingButton
                        type="submit"
                        theme={BUTTON_THEMES.PRIMARY}
                        isLoading={isUpdateNotificationLoading}
                      >
                        Update Notification
                      </LoadingButton>
                    </div>
                  </div>
                </footer>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NotificationUpdate;
